import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { css } from 'react-emotion';
import { Button, Select, Modal, RadioGroup, Radio, Tooltip, Checkbox } from '@cimpress/react-components';

import { ALL, INDIVIDUAL, NONE } from '../../../constants/subscriptionConstants';
import { getCounterType } from '../../../reducers/identities/utils';

const checkboxTextStyle = css`
  margin-left: 6px;
`;

const initialState = {
  subscriptionType: ALL,
  allIdentitiesSelected: false,
  selectedIdentities: [],
  selectedCounterParties: [],
  step: 0
};

class SubscriptionModal extends PureComponent {
  static propTypes = {
    identityType: PropTypes.string.isRequired,
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfigureMultipleIdentities: PropTypes.func.isRequired,
    identities: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
      })
    ).isRequired,
    counterParties: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
      })
    ).isRequired,
    notificationType: PropTypes.string
  };

  state = initialState;

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.show !== this.props.show) {
      this.setState({ ...initialState });
    }
  }

  onSelectIdentities = selectedIdentities => {
    this.setState({ selectedIdentities });
  };

  onSelectCounterParties = selectedCounterParties => {
    this.setState({ selectedCounterParties });
  };

  onChangeNotification = (e, value) => {
    this.setState({ subscriptionType: value });
  };

  onPrev = () => {
    this.setState({ step: 0 });
  };

  onNext = () => {
    this.setState({ step: 1 });
  };

  onAllIdentitiesSelected = () => {
    this.setState({ allIdentitiesSelected: !this.state.allIdentitiesSelected, selectedIdentities: [] });
  };

  onSave = () => {
    const { selectedIdentities, subscriptionType, selectedCounterParties, allIdentitiesSelected } = this.state;
    const { identityType, onConfigureMultipleIdentities, onClose } = this.props;

    onConfigureMultipleIdentities(
      subscriptionType,
      identityType,
      selectedIdentities.map(({ value }) => value),
      selectedCounterParties.map(({ value }) => value),
      allIdentitiesSelected
    );
    onClose();
  };

  render() {
    const { t, show, onClose, identityType, counterParties, identities, notificationType } = this.props;
    const { subscriptionType, allIdentitiesSelected, selectedIdentities, selectedCounterParties, step } = this.state;

    const typeIsIndividual = subscriptionType === INDIVIDUAL;
    const disableNext = selectedIdentities.length === 0 && !allIdentitiesSelected;

    const shownCounterParties = typeIsIndividual ? selectedCounterParties : [];
    const counterPartyType = getCounterType(identityType);
    const disableSave = typeIsIndividual && selectedCounterParties.length === 0;

    const selectIdentities = (
      <div>
        <Select
          label={t('settings:subscriptions.subscriptionModal.selectIdentities', { context: identityType })}
          value={selectedIdentities}
          options={identities}
          disabled={allIdentitiesSelected}
          onChange={this.onSelectIdentities}
          multi
        />
        <Checkbox
          className={checkboxTextStyle}
          label={t('settings:subscriptions.subscriptionModal.selectAllIdentities', { context: identityType })}
          checked={allIdentitiesSelected}
          onChange={this.onAllIdentitiesSelected}
        />
      </div>
    );

    const selectCounterParties = (
      <div>
        <RadioGroup name="counterParties" onChange={this.onChangeNotification} valueSelected={subscriptionType}>
          <Radio
            label={t('settings:subscriptions.actions.subscribeToAll', {
              context: `${counterPartyType}_${notificationType}`
            })}
            value={ALL}
          />
          <Radio
            label={t('settings:subscriptions.actions.unsubscribeToAll', {
              context: `${counterPartyType}_${notificationType}`
            })}
            value={NONE}
          />
          <Radio
            label={t('settings:subscriptions.actions.subscribeToIndividual', {
              context: `${counterPartyType}_${notificationType}`
            })}
            value={INDIVIDUAL}
          />
        </RadioGroup>
        <Select
          label={t('settings:subscriptions.selectLabel', { context: counterPartyType })}
          value={shownCounterParties}
          options={counterParties}
          disabled={!typeIsIndividual}
          onChange={this.onSelectCounterParties}
          multi
        />
      </div>
    );

    const saveButton = (
      <Button type="primary" disabled={disableSave} onClick={this.onSave}>
        {t('common:commonWords.save')}
      </Button>
    );

    const step0 = {
      title: t('settings:subscriptions.subscriptionModal.firstStepModalTitle', { context: identityType }),
      body: selectIdentities,
      footer: (
        <span>
          <Button type="default" onClick={onClose}>
            {t('common:commonWords.cancel')}
          </Button>{' '}
          <Button type="primary" disabled={disableNext} onClick={this.onNext}>
            {t('common:commonWords.next')}
          </Button>
        </span>
      )
    };

    const infoMsg = t('settings:subscriptions.selectLabel', { context: counterPartyType });

    const step1 = {
      title: t('settings:subscriptions.subscriptionModal.secondStepModalTitle', { context: identityType }),
      body: selectCounterParties,
      footer: (
        <span>
          <Button type="default" onClick={onClose}>
            {t('common:commonWords.cancel')}
          </Button>
          <Button type="default" onClick={this.onPrev}>
            {t('common:commonWords.back')}
          </Button>{' '}
          {disableSave ? saveButton : <Tooltip contents={infoMsg}>{saveButton}</Tooltip>}
        </span>
      )
    };

    const currentStep = step === 0 ? step0 : step1;

    return (
      <Modal
        className="modal-overflow"
        bsStyle={'info'}
        show={show}
        title={currentStep.title}
        footer={currentStep.footer}>
        {currentStep.body}
      </Modal>
    );
  }
}

export default withTranslation()(SubscriptionModal);
